import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Modal from '../Modal';
import _ from 'lodash';
import { toastr } from 'react-redux-toastr';
import DateFnsUtils from '@date-io/date-fns';
import enLocale from 'date-fns/locale/en-GB/index';
import Customer from '../customerForm/customer';
import {
    TITLES,
    GENDERS,
    PHONE_TYPE_OPTIONS,
    REFERRAL_SOURCES_TYPE,
    CUSTOMER_DEFAULT_FIELDS,
    LEADS_DEFAULT_FIELDS
} from '../../../../collums-constants/index';
import { isEmptyString, isValidEmail } from '../../../helpers';
import { isValidPhone } from '../../../helpers/validPhoneNumber';
import CustomerForm from '../customerForm/CustomerForm';
import CancelContinueModal from '../CancelContinueModal';
import OrganisationApi from '../../../api/organizationApi';
import { Container, Header, DuplicateWarning } from './styles';
import { checkDuplicate } from '../../../api/CustomerApi';

function CreateCustomerModal({
    isCreateCustomerModalOpen,
    isCreateCustomerModalAllowLead,
    hideCreateCustomerModal,
    persistCustomer,
    reloadCustomerList,
    clinic,
    className,
    isCreateCustomerModalAllowCustomer = true,
    initialCustomerData
}) {
    const formData = useRef({});
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [file, setFile] = useState(null);
    const computeAddAsLead =
        !isCreateCustomerModalAllowCustomer || (isCreateCustomerModalAllowLead !== false && !!initialCustomerData);
    const [addAsLead, setAddAsLead] = useState(computeAddAsLead);
    const inputNode = useRef(undefined);

    useEffect(() => {
        if (isCreateCustomerModalOpen) {
            setAddAsLead(computeAddAsLead);
        }
    }, [isCreateCustomerModalOpen, computeAddAsLead]);
    const [orgCustomerFields, setOrgCustomerFields] = useState(CUSTOMER_DEFAULT_FIELDS);
    const [orgLeadsFields, setOrgLeadsFields] = useState(LEADS_DEFAULT_FIELDS);
    const [duplicateWarnings, setDuplicateWarnings] = useState({ email: null, phone: null });
    const [orgSettingsLoaded, setOrgSettingsLoaded] = useState(false);

    const mergeWithDefaults = (savedFields, defaultFields) => {
        const defaultNames = new Set(defaultFields.map(f => f.name));
        const filtered = savedFields.filter(f => defaultNames.has(f.name));
        const savedNames = new Set(savedFields.map(f => f.name));
        const missingFields = defaultFields.filter(f => !savedNames.has(f.name));
        return [...filtered, ...missingFields];
    };

    useEffect(() => {
        if (isCreateCustomerModalOpen) {
            setDuplicateWarnings({ email: null, phone: null });
        }
    }, [isCreateCustomerModalOpen]);

    // Save stays disabled until organisation field settings are loaded; reopening the modal retries a failed request.
    useEffect(() => {
        if (!isCreateCustomerModalOpen || orgSettingsLoaded) return;
        OrganisationApi.getOrg()
            .then(org => {
                if (org.customerFields && org.customerFields.length > 0) {
                    setOrgCustomerFields(mergeWithDefaults(org.customerFields, CUSTOMER_DEFAULT_FIELDS));
                }
                if (org.leadsFields && org.leadsFields.length > 0) {
                    setOrgLeadsFields(mergeWithDefaults(org.leadsFields, LEADS_DEFAULT_FIELDS));
                }
                setOrgSettingsLoaded(true);
            })
            .catch(() => toastr.error('Could not load organisation settings. Please try again.'));
    }, [isCreateCustomerModalOpen, orgSettingsLoaded]);

    const effectiveCustomerFields = addAsLead ? orgLeadsFields : orgCustomerFields;

    const handleDuplicateCheck = useCallback(
        async (field, value) => {
            if (!addAsLead) return;
            if (!value || value.trim().length < 3) {
                setDuplicateWarnings(prev => ({ ...prev, [field]: null }));
                return;
            }
            try {
                const params = field === 'email' ? { email: value } : { phone: value };
                const result = await checkDuplicate(params);
                const messages = [];
                if (result.patientExists) messages.push('client');
                if (result.leadExists) messages.push('lead');
                if (messages.length > 0) {
                    const fieldLabel = field === 'email' ? 'email' : 'phone number';
                    setDuplicateWarnings(prev => ({
                        ...prev,
                        [field]: `Just to let you know, a ${messages.join(
                            ' and '
                        )} with this ${fieldLabel} already exists.`
                    }));
                } else {
                    setDuplicateWarnings(prev => ({ ...prev, [field]: null }));
                }
            } catch (e) {
                setDuplicateWarnings(prev => ({ ...prev, [field]: null }));
            }
        },
        [addAsLead]
    );

    const save = async () => {
        if (orgSettingsLoaded && validadeForm()) {
            const result = await persistCustomer({ ...formData.current, addAsLead: addAsLead }, file, clinic || null);
            if (!result) {
                reloadCustomerList();
                hideCreateCustomerModal();
            }
        }
    };

    const getFieldValue = fieldName => {
        if (fieldName === 'otherPhoneType') return formData.current.otherPhone?.[0]?.type;
        if (fieldName === 'referredBy') return formData.current.referredBy?.id || formData.current.referredBy;
        if (fieldName === 'country') return formData.current.country?.value;
        return formData.current[fieldName];
    };

    const validadeForm = () => {
        const errors = [];
        for (const field of effectiveCustomerFields) {
            if (!field.required || !field.show) continue;
            // "Referred by" is disabled unless the referral source is "Friend".
            if (field.name === 'referredBy' && formData.current.referralSource !== REFERRAL_SOURCES_TYPE.FRIEND) {
                continue;
            }
            const value = getFieldValue(field.name);
            if (field.name === 'email') {
                if (!isValidEmail(formData.current.email || '')) {
                    errors.push('Need to add a valid email');
                }
                continue;
            }
            if (field.name === 'mobilePhone') {
                if (isEmptyString(formData.current.mobilePhone)) {
                    errors.push('Need to add a phone number.');
                } else if (!isValidPhone(formData.current.mobilePhone || '')) {
                    errors.push('Need to add a valid phone number');
                }
                continue;
            }
            if (!value || (typeof value === 'string' && isEmptyString(value))) {
                const defaults = addAsLead ? LEADS_DEFAULT_FIELDS : CUSTOMER_DEFAULT_FIELDS;
                const displayLabel = (defaults.find(f => f.name === field.name) || field).label;
                errors.push(`${displayLabel} is required.`);
            }
        }
        if (errors.length > 0) {
            errors.forEach(err => toastr.error(err));
            return false;
        }
        if (
            !addAsLead &&
            (_.isEmpty(formData.current.country) ||
                formData.current.country.label === '' ||
                formData.current.country.value === '')
        ) {
            formData.current.country = {
                value: 'GB',
                label: 'United Kingdom'
            };
        }
        if (
            addAsLead &&
            (_.isEmpty(formData.current.country) ||
                formData.current.country.label === '' ||
                formData.current.country.value === '')
        ) {
            formData.current.country = {
                value: '',
                label: ''
            };
        }
        if (formData.current.blockOnlineBooking && !formData.current.blockOnlineText?.trim()) {
            toastr.error('Need to add a text to show when online booking is blocked.');
            return false;
        }

        return true;
    };

    const cancel = () => {
        if (!_.isEqual(_.omit(formData.current, ['dateOfBirth']), _.omit(Customer(), ['dateOfBirth']))) {
            setIsConfirmModalOpen(true);
            return;
        }

        hideCreateCustomerModal();
    };

    return (
        <Container className={className}>
            <Modal
                isOpen={isCreateCustomerModalOpen}
                title=""
                size="md"
                confirmLabel="Save"
                onCancel={cancel}
                onConfirm={save}
                disableConfirmButton={!orgSettingsLoaded}
                onClose={cancel}
            >
                <Header>
                    {isCreateCustomerModalAllowCustomer && (
                        <button className={addAsLead ? '' : 'active'} onClick={() => setAddAsLead(false)}>
                            Add a new client
                        </button>
                    )}
                    &nbsp;&nbsp;&nbsp;&nbsp;
                    {isCreateCustomerModalAllowLead && (
                        <button className={addAsLead ? 'active' : ''} onClick={() => setAddAsLead(true)}>
                            Add a new lead
                        </button>
                    )}
                </Header>
                {addAsLead && duplicateWarnings.email && <DuplicateWarning>{duplicateWarnings.email}</DuplicateWarning>}
                {addAsLead && duplicateWarnings.phone && <DuplicateWarning>{duplicateWarnings.phone}</DuplicateWarning>}

                <CustomerForm
                    key={initialCustomerData ? 'with-initial' : 'without-initial'}
                    DateFnsUtils={DateFnsUtils}
                    enLocale={enLocale}
                    formData={formData}
                    inputNode={inputNode}
                    TITLES={TITLES}
                    GENDERS={GENDERS}
                    setFile={setFile}
                    PHONE_TYPE_OPTIONS={PHONE_TYPE_OPTIONS}
                    REFERRAL_SOURCES_TYPE={REFERRAL_SOURCES_TYPE}
                    addAsLead={addAsLead}
                    onFieldBlur={addAsLead ? handleDuplicateCheck : undefined}
                    customer={initialCustomerData}
                />
                {isConfirmModalOpen && (
                    <CancelContinueModal
                        setOpen={setIsConfirmModalOpen}
                        onContinue={() => {
                            hideCreateCustomerModal();
                        }}
                        closeOnBlur={false}
                        title="Your changes will not be saved"
                        contentText="Are you sure you want to continue?"
                    />
                )}
            </Modal>
        </Container>
    );
}

CreateCustomerModal.propTypes = {
    isCreateCustomerModalOpen: PropTypes.bool.isRequired,
    isCreateCustomerModalAllowLead: PropTypes.bool,
    isCreateCustomerModalAllowCustomer: PropTypes.bool,
    hideCreateCustomerModal: PropTypes.func.isRequired,
    persistCustomer: PropTypes.func.isRequired,
    reloadCustomerList: PropTypes.func.isRequired,
    clinic: PropTypes.string,
    className: PropTypes.string,
    initialCustomerData: PropTypes.object
};

export default CreateCustomerModal;
